/**
 * Live previews for the Catalog page, rendered from the SAME pure helpers the
 * customer app uses (@mitang/shared display.ts) — so what the admin sees here
 * is exactly what dealers get. Both take the PENDING (unsaved) editor state.
 */
import type { SpecTableRow } from '@mitang/shared';

/** Phone-styled mock of the product detail screen's spec card. */
export function PhonePreview({
  category,
  modelNumber,
  partNo,
  rows,
  sampleMissing,
}: {
  category: string;
  modelNumber: string;
  partNo?: string;
  rows: SpecTableRow[];
  sampleMissing: boolean;
}): JSX.Element {
  return (
    <div className="w-[290px] shrink-0">
      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[.14em] text-slate-400">
        App preview{sampleMissing ? ' · no product synced yet' : ''}
      </p>
      <div className="overflow-hidden rounded-[26px] border-[6px] border-slate-800 bg-slate-100 shadow-lg">
        <div className="max-h-[430px] overflow-y-auto px-3.5 pb-4 pt-3">
          <p className="text-[9px] font-bold uppercase tracking-[.12em] text-slate-400">{category}</p>
          <h3 className="mt-0.5 text-[17px] font-extrabold leading-tight tracking-tight text-slate-900">
            {modelNumber}
          </h3>
          {partNo ? (
            <div className="mt-0.5 flex items-center gap-1.5">
              <span className="font-mono text-[10px] text-slate-400">{partNo}</span>
              <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[8px] font-bold text-blue-600">COPY</span>
            </div>
          ) : null}
          <div className="mt-3 rounded-[14px] bg-white px-3.5 py-1 shadow-sm">
            {rows.map((row, i) => (
              <div key={`${row.key}-${i}`}>
                {row.breakBefore ? <div className="-mx-3.5 h-1.5 bg-slate-100" /> : null}
                <div
                  className={`flex justify-between gap-3 py-2 text-[11px] ${
                    i > 0 && !row.breakBefore ? 'border-t border-slate-100' : ''
                  }`}
                >
                  <span className="shrink-0 uppercase text-slate-400">{row.label}</span>
                  <span className="text-right font-semibold text-slate-900">{row.value}</span>
                </div>
              </div>
            ))}
            {rows.length === 0 ? (
              <p className="py-3 text-center text-[11px] text-slate-400">No columns shown</p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/** WhatsApp-styled bubble showing the copy/share message (renders *bold*). */
export function ShareMessagePreview({ text }: { text: string }): JSX.Element {
  return (
    <div className="w-[290px] shrink-0">
      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[.14em] text-slate-400">
        Message preview (WhatsApp / copy)
      </p>
      <div className="rounded-2xl bg-[#e5ddd5] p-3">
        <div className="ml-6 whitespace-pre-line rounded-lg rounded-tr-none bg-[#dcf8c6] px-3 py-2 text-[12px] leading-relaxed text-slate-800 shadow-sm">
          {renderBold(text)}
        </div>
      </div>
    </div>
  );
}

/** Render WhatsApp's *bold* markup as <strong> for the preview. */
function renderBold(text: string): Array<JSX.Element | string> {
  const parts = text.split(/\*([^*\n]+)\*/g);
  return parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));
}
